import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import PublicFaq from '../../components/PublicFaq';
import axios from 'axios';
import { Loader2, ArrowLeft, X, ShieldCheck, ExternalLink, CheckCircle2, Plus, Play, Mic } from 'lucide-react';
import { useCompare, MAX_COMPARE } from '../../hooks/useCompare';
import { LogoOrMark } from '../../components/Lettermark';
import { Dialog, DialogContent } from '../../components/ui/dialog';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../../components/ui/tooltip';
import { ShareButton } from '../../components/ShareButton';
import { ORIGIN, setSocialPreview } from '../../lib/seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_LABEL = { live: 'Live', onboarding: 'Coming soon', piloting: 'Piloting', in_development: 'In development', concept: 'Concept' };

const ytId = (url) => {
  const m = (url || '').match(/(?:youtube\.com\/embed\/|youtu\.be\/|youtube\.com\/watch\?v=)([\w-]+)/);
  return m ? m[1] : null;
};
const ytThumb = (url) => { const id = ytId(url); return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null; };
const ytEmbed = (url) => { const id = ytId(url); return id ? `https://www.youtube.com/embed/${id}?autoplay=1&rel=0` : null; };

const Compare = () => {
  const { slugs, remove, clear } = useCompare();
  const [sp] = useSearchParams();
  const urlSlugs = (sp.get('slugs') || '').split(',').map((x) => x.trim()).filter(Boolean);
  const active = urlSlugs.length ? urlSlugs : slugs;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [videoUrl, setVideoUrl] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (active.length === 0) { setItems([]); setLoading(false); return; }
    setLoading(true);
    axios.get(`${API}/public/compare`, { params: { slugs: active.join(','), ...(sessionStorage.getItem('ctc_demo') === '1' ? { demo: 1 } : {}) } })
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [active.join(',')]);  // eslint-disable-line react-hooks/exhaustive-deps

  const removeOne = (slug) => {
    remove(slug);
    const next = active.filter((s) => s !== slug);
    if (urlSlugs.length) navigate(next.length ? `/compare?slugs=${next.join(',')}` : '/compare');
  };
  const clearAll = () => { clear(); navigate('/compare'); };

  const compareSlugs = active.join(',');
  useEffect(() => {
    if (!active.length) return;
    const names = items.map((i) => i.name).filter(Boolean);
    const title = names.length >= 2
      ? `Compare ${names.slice(0, 3).join(', ')}${names.length > 3 ? ` +${names.length - 3} more` : ''} | Care Tech Compare`
      : 'Compare Care Technology Suppliers | Care Tech Compare';
    setSocialPreview({
      title,
      image: `${ORIGIN}/api/public/share/compare/image.jpg?slugs=${compareSlugs}`,
      url: `${ORIGIN}/compare?slugs=${compareSlugs}`,
    });
  }, [compareSlugs, items]);  // eslint-disable-line react-hooks/exhaustive-deps

  const VideoCell = ({ s }) => {
    const url = (() => { const v = (s.videos || [])[0]; return v && (v.url || v); })();
    const thumb = ytThumb(url);
    if (!thumb) return <span className="text-gray-400 text-sm">-</span>;
    return (
      <button type="button" onClick={() => setVideoUrl(url)} className="relative block w-full aspect-video rounded-md overflow-hidden bg-gray-900 group max-w-[220px]" data-testid={`compare-video-${s.slug}`}>
        <img src={thumb} alt={`${s.name} video`} className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity" onError={(e) => { e.target.parentElement.style.display = 'none'; }} />
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="w-9 h-9 rounded-full bg-white/90 flex items-center justify-center shadow group-hover:scale-105 transition-transform"><Play className="w-4 h-4 text-[#00A79D] fill-[#00A79D] ml-0.5" /></span>
        </span>
      </button>
    );
  };

  const dash = <span className="text-gray-400 text-sm">-</span>;
  const fields = [
    { label: 'Summary', render: (s) => <span className="text-sm text-gray-600">{s.tagline || '-'}</span> },
    { label: 'Video', render: (s) => <VideoCell s={s} /> },
    { label: 'Podcast', render: (s) => (s.podcast && s.podcast.post_url
        ? <Link to={s.podcast.post_url} className="inline-flex items-center gap-1.5 text-sm text-[#1A3A5F] font-semibold hover:text-[#00A79D]" data-testid={`compare-podcast-${s.slug}`}><Mic className="w-3.5 h-3.5" /> Listen to episode</Link>
        : dash) },
    { label: 'Status', render: (s) => <span className="text-sm">{STATUS_LABEL[s.status] || s.status}</span> },
    { label: 'Categories', render: (s) => ((s.all_categories || []).length
        ? <div className="flex flex-wrap gap-1">{s.all_categories.slice(0, 10).map((c) => <span key={c} className="text-[11px] text-gray-700 bg-gray-100 px-2 py-0.5 rounded">{c}</span>)}</div>
        : dash) },
    { label: 'Products', render: (s) => ((s.products || []).length
        ? <ul className="space-y-1.5">{s.products.map((p, i) => <li key={i} className="text-sm text-gray-700"><span className="font-medium">{p.name}</span></li>)}</ul>
        : dash) },
    { label: 'Accreditations', render: (s) => ((s.accreditations || []).length
        ? <div className="flex flex-wrap gap-1">{s.accreditations.map((a) => <span key={a} className="text-[11px] text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />{a}</span>)}</div>
        : dash) },
    { label: 'Website', render: (s) => (s.website
        ? <a href={s.website} target="_blank" rel="noreferrer" className="text-[#00A79D] text-sm inline-flex items-center gap-1 hover:underline">Visit <ExternalLink className="w-3.5 h-3.5" /></a>
        : dash) },
  ];

  const Badges = ({ s }) => (
    <div className="flex flex-wrap gap-1 mt-1">
      {s.tier_label && s.tier && s.tier !== 'dormant' && <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-white bg-[#1A3A5F] px-2 py-0.5 rounded-full" data-testid={`compare-tier-${s.slug}`}><ShieldCheck className="w-3 h-3" /> {s.tier_label}</span>}
      {(s.supplier_reviewed || s.product_reviewed) && (
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full cursor-help" data-testid={`compare-assured-${s.slug}`}><CheckCircle2 className="w-3 h-3" /> Independently Assured</span>
            </TooltipTrigger>
            <TooltipContent style={{ color: '#ffffff', backgroundColor: '#1A3A5F' }} className="max-w-xs leading-relaxed py-2 px-3 border-0" data-testid={`compare-assured-${s.slug}-tooltip`}>
              Independently checked and assured by Care City. Assurance is earned, never bought - it can't be purchased.
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );

  return (
    <div data-testid="page-compare">
      <div className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Link to="/browse" className="text-sm text-gray-500 hover:text-[#00A79D] inline-flex items-center gap-1.5 mb-4"><ArrowLeft className="w-4 h-4" /> Back to directory</Link>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-extrabold text-gray-900">Compare suppliers</h1>
              <p className="text-gray-600 mt-2">Weigh up to {MAX_COMPARE} care-tech suppliers side by side.</p>
            </div>
            {active.length > 0 && (
              <div className="flex items-center gap-4">
                <ShareButton
                  shareUrl={`${API}/public/share/compare?slugs=${compareSlugs}`}
                  title="Compare care tech suppliers | Care Tech Compare"
                  label="Share comparison"
                  testid="compare-share-link"
                  className="inline-flex items-center gap-1.5 border border-[#E5E7EB] hover:border-[#00A79D] text-gray-700 hover:text-[#00857D] font-semibold text-sm px-4 py-2 rounded-md transition-colors"
                />
                <button onClick={clearAll} className="text-sm text-gray-500 hover:text-red-600 font-medium" data-testid="compare-clear-all">Clear all</button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="py-24 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
        ) : items.length === 0 ? (
          <div className="py-20 text-center" data-testid="compare-empty">
            <p className="text-gray-500">You haven't picked any suppliers to compare yet.</p>
            <p className="text-gray-400 text-sm mt-1">Add up to {MAX_COMPARE} suppliers to see them side by side.</p>
            <button onClick={() => navigate('/browse')} className="inline-flex items-center gap-1.5 bg-[#00A79D] hover:bg-[#00857D] text-white font-semibold px-5 py-2.5 rounded-md mt-5 transition-colors" data-testid="compare-browse-btn">
              <Plus className="w-4 h-4" /> Add suppliers
            </button>
          </div>
        ) : (
          <>
            {items.length < MAX_COMPARE && (
              <div className="mb-5 flex items-center justify-between gap-3 flex-wrap bg-[#00A79D]/5 border border-dashed border-[#00A79D]/40 rounded-lg px-4 py-3" data-testid="compare-add-hint">
                <p className="text-sm text-gray-600">Comparing <span className="font-semibold text-gray-900">{items.length}</span> of {MAX_COMPARE}. Add {MAX_COMPARE - items.length} more for a fuller picture.</p>
                <Link to="/browse" className="inline-flex items-center gap-1.5 text-[#00A79D] hover:text-[#00857D] font-semibold text-sm shrink-0" data-testid="compare-add-more"><Plus className="w-4 h-4" /> Add another supplier</Link>
              </div>
            )}
            {/* Desktop / tablet: side-by-side table */}
            <div className="hidden md:block overflow-x-auto border border-[#E5E7EB] rounded-lg bg-white">
              <table className="w-full border-collapse" data-testid="compare-table">
                <thead>
                  <tr>
                    <th className="sticky left-0 bg-[#F9FAFB] z-10" />
                    {items.map((s) => (
                      <th key={s.slug} className="p-4 text-left min-w-[240px] border-l border-[#E5E7EB]" data-testid={`compare-col-${s.slug}`}>
                        <div className="flex items-start justify-between gap-2">
                          <LogoOrMark logo={s.logo} name={s.name} imgClassName="h-8 w-auto max-w-[130px] object-contain" markClassName="h-8 w-8 text-xs rounded-md" />
                          <button onClick={() => removeOne(s.slug)} className="text-gray-400 hover:text-red-600" data-testid={`compare-remove-${s.slug}`}><X className="w-4 h-4" /></button>
                        </div>
                        <Link to={`/suppliers/${s.slug}`} className="block font-semibold text-gray-900 mt-2 hover:text-[#00A79D]">{s.name}</Link>
                        <Badges s={s} />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {fields.map((f) => (
                    <tr key={f.label} className="border-t border-[#E5E7EB] align-top">
                      <th className="text-left text-sm font-semibold text-gray-500 py-4 pr-4 pl-4 w-40 sticky left-0 bg-[#F9FAFB]">{f.label}</th>
                      {items.map((s) => <td key={s.slug} className="py-4 px-4 border-l border-[#E5E7EB]">{f.render(s)}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile: stacked supplier cards */}
            <div className="md:hidden space-y-5" data-testid="compare-mobile">
              {items.map((s) => (
                <div key={s.slug} className="border border-[#E5E7EB] rounded-lg bg-white overflow-hidden" data-testid={`compare-card-${s.slug}`}>
                  <div className="p-4 border-b border-[#F1F1F4] flex items-start gap-3">
                    <LogoOrMark logo={s.logo} name={s.name} imgClassName="h-10 w-auto max-w-[120px] object-contain" markClassName="h-10 w-10 text-sm rounded-md" />
                    <div className="flex-1 min-w-0">
                      <Link to={`/suppliers/${s.slug}`} className="block font-semibold text-gray-900 hover:text-[#00A79D]">{s.name}</Link>
                      <Badges s={s} />
                    </div>
                    <button onClick={() => removeOne(s.slug)} className="text-gray-400 hover:text-red-600 shrink-0" data-testid={`compare-remove-m-${s.slug}`}><X className="w-4 h-4" /></button>
                  </div>
                  <dl className="divide-y divide-[#F1F1F4]">
                    {fields.map((f) => (
                      <div key={f.label} className="px-4 py-3">
                        <dt className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1.5">{f.label}</dt>
                        <dd>{f.render(s)}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Video lightbox */}
      <Dialog open={!!videoUrl} onOpenChange={(o) => { if (!o) setVideoUrl(null); }}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden bg-black border-0" data-testid="compare-video-modal">
          {videoUrl && (
            <>
              <div className="aspect-video w-full">
                <iframe className="w-full h-full" src={ytEmbed(videoUrl)} title="Supplier video" allow="autoplay; encrypted-media; fullscreen" allowFullScreen />
              </div>
              <a href={videoUrl} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-1.5 text-xs text-gray-300 hover:text-white py-2.5 bg-black">Watch on YouTube <ExternalLink className="w-3.5 h-3.5" /></a>
            </>
          )}
        </DialogContent>
      </Dialog>

      <PublicFaq id="compare" heading="About comparing care technology"
        intro="How the Care Tech Compare comparison tool works and how to get the most from it."
        faqs={[
          { q: 'How does the comparison tool work?', a: 'Add any care technology products to your comparison tray as you browse, then view them side by side. You can compare key details like category, care setting, pricing model, features and supplier information in a single table to see how each option stacks up.' },
          { q: 'Is the comparison unbiased?', a: 'Yes. Care Tech Compare is independent and takes no commission on sales, so products are presented on equal terms. What you see is based on the information suppliers provide and genuine reviews from care providers - not on who pays the most.' },
          { q: 'How many products can I compare at once?', a: 'You can line up several products at a time so you can weigh the realistic shortlist for your service. If you are not sure which to compare, use our free advisor to get an independent, tailored shortlist first.' },
          { q: 'Do I need an account to compare products?', a: 'No. Comparing is free and needs no sign-up. Create a free account only when you want to save shortlists, message suppliers directly or leave a review.' },
        ]} />
    </div>
  );
};

export default Compare;
